/**
 * Billing Page
 * Main billing dashboard with tabs for subscription, credits, and usage
 * JIRA: SPLATFORM-42
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@bri/shared-components';
import { useWorkspace } from '@/lib/contexts/useWorkspace';
import { AppHeader } from '@/components/layout/AppHeader';
import { AppSidebar } from '@/components/layout/AppSidebar';
import { Card } from '@/components/ui/Card';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import {
  CreditBalance,
  SubscriptionCard,
  CreditPackCard,
  WorkspaceCreditDisplay,
  CreditAllocationModal,
  CreditRevocationModal,
  TransactionHistory,
} from '@bri/shared-components';
import {
  CreditCardIcon,
  CurrencyDollarIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { billingService, type UserCredits, type CreditPack, type WorkspaceCredits, type Transaction } from '@/lib/services/billingService';
import type { CreditAllocation } from '@/lib/services/billingService';
import { CreditPurchaseModal } from '@/components/billing/CreditPurchaseModal';
import { SubscriptionPurchaseModal } from '@/components/billing/SubscriptionPurchaseModal';
import { SubscriptionCancellationModal } from '@/components/billing/SubscriptionCancellationModal';
import { getErrorMessage } from '@/lib/utils';
import type { Subscription } from '@/lib/services/billingService';

type TabType = 'subscription' | 'credits' | 'usage';

export default function BillingPage() {
  const { user } = useAuth();
  const { workspaces } = useWorkspace();

  const [activeTab, setActiveTab] = useState<TabType>('subscription');
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);

  // Data state
  const [userCredits, setUserCredits] = useState<UserCredits | null>(null);
  const [_subscription, setSubscription] = useState<Subscription | null>(null);
  const [creditPacks, setCreditPacks] = useState<CreditPack[]>([]);
  const [workspaceCredits, setWorkspaceCredits] = useState<Record<string, WorkspaceCredits>>({});
  const [workspaceRevocableCredits, setWorkspaceRevocableCredits] = useState<Record<string, number>>({});
  const [transactions, setTransactions] = useState<Transaction[]>([]);

  // Modal state
  const [allocationModal, setAllocationModal] = useState<{
    isOpen: boolean;
    workspaceId: string | null;
    workspaceName: string;
  }>({
    isOpen: false,
    workspaceId: null,
    workspaceName: '',
  });

  const [revocationModal, setRevocationModal] = useState<{
    isOpen: boolean;
    workspaceId: string | null;
    workspaceName: string;
    maxCredits: number;
  }>({
    isOpen: false,
    workspaceId: null,
    workspaceName: '',
    maxCredits: 0,
  });

  const [purchaseModal, setPurchaseModal] = useState<{
    isOpen: boolean;
    clientSecret: string | null;
    pack: CreditPack | null;
  }>({
    isOpen: false,
    clientSecret: null,
    pack: null,
  });

  const [subscriptionModal, setSubscriptionModal] = useState<{
    isOpen: boolean;
    clientSecret: string | null;
    tier: { name: string; tier: 'PRO' | 'ENTERPRISE'; price: string; credits: number } | null;
  }>({
    isOpen: false,
    clientSecret: null,
    tier: null,
  });

  const [cancelModal, setCancelModal] = useState<{
    isOpen: boolean;
  }>({
    isOpen: false,
  });

  const loadBillingData = useCallback(async () => {
    setIsLoading(true);
    try {
      // Load user credits, subscription, and transaction history in parallel
      const [creditsData, subscriptionData, packsData, transactionsData, allocationsData] = await Promise.all([
        billingService.getUserCredits(),
        billingService.getSubscription(),
        billingService.getCreditPacks(),
        billingService.getTransactionHistory(100),
        billingService.getAllocations(),
      ]);

      setUserCredits(creditsData);
      setSubscription(subscriptionData.data?.subscription);
      setCreditPacks(packsData);
      setTransactions(transactionsData);
      setWorkspaceRevocableCredits(
        allocationsData.reduce<Record<string, number>>((acc, allocation: CreditAllocation) => {
          if (!allocation.workspace_id) {
            return acc;
          }

          acc[allocation.workspace_id] = (acc[allocation.workspace_id] || 0) + allocation.amount;
          return acc;
        }, {})
      );

      // Load workspace credits for all workspaces
      if (workspaces.length > 0) {
        const workspaceCreditsPromises = workspaces.map(async (ws) => {
          try {
            const credits = await billingService.getWorkspaceCredits(ws.workspaceId);
            return { workspaceId: ws.workspaceId, credits };
          } catch (error) {
            console.error(`Failed to load credits for workspace ${ws.workspaceId}`, error);
            return null;
          }
        });

        const results = await Promise.all(workspaceCreditsPromises);
        const creditsMap: Record<string, WorkspaceCredits> = {};
        results.forEach((result) => {
          if (result) {
            creditsMap[result.workspaceId] = result.credits;
          }
        });
        setWorkspaceCredits(creditsMap);
      }
    } catch (error: unknown) {
      console.error('Error loading billing data:', error);
      toast.error(getErrorMessage(error) || 'Failed to load billing data');
    } finally {
      setIsLoading(false);
    }
  }, [workspaces]);

  useEffect(() => {
    if (user) {
      void loadBillingData();
    }
  }, [user, loadBillingData]);

  const handleUpgradeToTier = async (tier: 'PRO' | 'ENTERPRISE') => {
    setIsProcessing(true);
    try {
      toast.loading('Initializing subscription...', { id: 'subscribe' });

      // Call the real Stripe endpoint to create subscription with payment intent
      const response = await billingService.createSubscriptionPaymentIntent(tier);

      if (!response.success) {
        throw new Error(response.error || 'Failed to initialize subscription');
      }

      const { paymentIntent, tier: tierInfo } = response.data;

      // Open Stripe payment modal
      setSubscriptionModal({
        isOpen: true,
        clientSecret: paymentIntent.clientSecret,
        tier: tierInfo,
      });

      toast.dismiss('subscribe');
    } catch (error: unknown) {
      console.error('Error initializing subscription:', error);
      toast.error(getErrorMessage(error) || 'Failed to initialize subscription', { id: 'subscribe' });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancelSubscription = () => {
    setCancelModal({ isOpen: true });
  };

  const handleConfirmCancellation = async () => {

    setIsProcessing(true);
    try {
      toast.loading('Cancelling subscription...', { id: 'cancel' });
      await billingService.cancelSubscription();
      toast.success('Subscription cancelled. You will have access until the end of your billing period.', { id: 'cancel' });
      setCancelModal({ isOpen: false });
      await loadBillingData();
    } catch (error: unknown) {
      console.error('Error cancelling subscription:', error);
      toast.error(getErrorMessage(error) || 'Failed to cancel subscription', { id: 'cancel' });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleManageSubscription = async () => {
    setIsProcessing(true);
    try {
      const portalUrl = await billingService.getPortalUrl(window.location.href);
      window.location.href = portalUrl;
    } catch (error: unknown) {
      console.error('Error opening portal:', error);
      toast.error(getErrorMessage(error) || 'Failed to open billing portal');
    } finally {
      setIsProcessing(false);
    }
  };

  const handlePurchaseCreditPack = async (packId: string) => {
    setIsProcessing(true);
    try {
      toast.loading('Initializing payment...', { id: 'purchase' });

      // Call the real Stripe endpoint to create payment intent
      const response = await billingService.purchaseCredits(packId);

      if (!response.success) {
        throw new Error(response.error || 'Failed to initialize payment');
      }

      const { paymentIntent, pack } = response.data;

      // Open Stripe payment modal
      setPurchaseModal({
        isOpen: true,
        clientSecret: paymentIntent.clientSecret,
        pack,
      });

      toast.dismiss('purchase');
    } catch (error: unknown) {
      console.error('Error initializing credit purchase:', error);
      toast.error(getErrorMessage(error) || 'Failed to initialize payment', { id: 'purchase' });
    } finally {
      setIsProcessing(false);
    }
  };

  const handlePurchaseSuccess = async () => {
    setPurchaseModal({ isOpen: false, clientSecret: null, pack: null });
    toast.success('Credits purchased successfully!');
    // Reload billing data to show new credits
    await loadBillingData();
  };

  const handlePurchaseCancel = () => {
    setPurchaseModal({ isOpen: false, clientSecret: null, pack: null });
  };

  const handleSubscriptionSuccess = async () => {
    setSubscriptionModal({ isOpen: false, clientSecret: null, tier: null });
    toast.success('Subscription activated successfully!');
    // Reload billing data to show new subscription and credits
    await loadBillingData();
  };

  const handleSubscriptionCancel = () => {
    setSubscriptionModal({ isOpen: false, clientSecret: null, tier: null });
  };

  const handleAllocateCredits = async (workspaceId: string) => {
    // Find workspace name
    const workspace = workspaces.find(ws => ws.workspaceId === workspaceId);
    if (!workspace) {
      toast.error('Workspace not found');
      return;
    }

    // Open modal
    setAllocationModal({
      isOpen: true,
      workspaceId,
      workspaceName: workspace.name,
    });
  };

  const handleModalAllocate = async (amount: number) => {
    if (!allocationModal.workspaceId) return;

    setIsProcessing(true);
    try {
      await billingService.allocateCredits(allocationModal.workspaceId, amount);
      toast.success(`Allocated ${amount} credits to workspace!`);
      await loadBillingData(); // Reload data
    } catch (error: unknown) {
      console.error('Error allocating credits:', error);
      toast.error(getErrorMessage(error) || 'Failed to allocate credits');
      throw error; // Re-throw so modal can handle it
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRevokeCredits = async (workspaceId: string) => {
    const revocableCredits = workspaceRevocableCredits[workspaceId] || 0;
    if (revocableCredits === 0) {
      toast.error('No credits allocated by you are available to revoke');
      return;
    }

    // Find workspace name
    const workspace = workspaces.find(ws => ws.workspaceId === workspaceId);
    if (!workspace) {
      toast.error('Workspace not found');
      return;
    }

    // Open modal
    setRevocationModal({
      isOpen: true,
      workspaceId,
      workspaceName: workspace.name,
      maxCredits: revocableCredits,
    });
  };

  const handleModalRevoke = async (amount: number) => {
    if (!revocationModal.workspaceId) return;

    setIsProcessing(true);
    try {
      await billingService.revokeCredits(revocationModal.workspaceId, amount);
      toast.success(`Revoked ${amount} credits from workspace!`);
      await loadBillingData(); // Reload data
    } catch (error: unknown) {
      console.error('Error revoking credits:', error);
      toast.error(getErrorMessage(error) || 'Failed to revoke credits');
      throw error; // Re-throw so modal can handle it
    } finally {
      setIsProcessing(false);
    }
  };

  const tabs = [
    { id: 'subscription' as TabType, name: 'Subscription', icon: CreditCardIcon },
    { id: 'credits' as TabType, name: 'Credits', icon: CurrencyDollarIcon },
    { id: 'usage' as TabType, name: 'Usage', icon: ChartBarIcon },
  ];

  const tiers = billingService.getSubscriptionTiers();
  const currentTier = userCredits?.subscriptionTier || 'FREE';

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <AppHeader />
        <div className="flex flex-1">
          <AppSidebar />
          <main className="flex-1 flex items-center justify-center">
            <LoadingSpinner size="lg" />
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header - full width at top */}
      <AppHeader />

      {/* Content area with sidebar */}
      <div className="flex flex-1">
        <AppSidebar />

        {/* Main content */}
        <main className="flex-1 px-4 py-8 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-gray-900">Billing & Subscription</h1>
              <p className="text-gray-600 mt-1">Manage your subscription, credits, and usage</p>
            </div>

            {/* Credit Balance Overview */}
            {userCredits && (
              <Card className="p-6 mb-8">
                <CreditBalance
                  available={userCredits.creditsAvailable}
                  total={userCredits.totalCreditsOwned}
                  allocated={userCredits.creditsAllocatedToWorkspaces}
                  size="lg"
                  showDetails={true}
                />
              </Card>
            )}

            {/* Tabs */}
            <div className="border-b border-gray-200 mb-8">
              <nav className="-mb-px flex space-x-8">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm ${activeTab === tab.id
                        ? 'border-primary text-primary'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                    >
                      <Icon className="h-5 w-5" />
                      {tab.name}
                    </button>
                  );
                })}
              </nav>
            </div>

            {/* Tab Content */}
            <div className="space-y-8">
              {/* Subscription Tab */}
              {activeTab === 'subscription' && (
                <>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-6">Choose Your Plan</h2>
                    <div className="grid gap-6 lg:grid-cols-3">
                      {tiers.map((tier) => (
                        <SubscriptionCard
                          key={tier.tier}
                          tier={{
                            ...tier,
                            current: tier.tier === currentTier,
                            popular: tier.tier === 'PRO',
                            cancelAtPeriodEnd: _subscription?.cancel_at_period_end,
                            currentPeriodEnd: _subscription?.current_period_end,
                          }}
                          onSelect={() => handleUpgradeToTier(tier.tier as 'PRO' | 'ENTERPRISE')}
                          onManage={tier.tier !== 'FREE' ? handleManageSubscription : undefined}
                          onCancel={tier.tier === currentTier && tier.tier !== 'FREE' ? handleCancelSubscription : undefined}
                          disabled={isProcessing}
                        />
                      ))}
                    </div>
                  </div>
                </>
              )}

              {/* Credits Tab */}
              {activeTab === 'credits' && (
                <>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-6">Purchase Credit Packs</h2>
                    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                      {creditPacks.map((pack, index) => (
                        <CreditPackCard
                          key={pack.id}
                          pack={{
                            ...pack,
                            popular: index === 1, // Middle pack is popular
                            savings: index === 2 ? '15%' : undefined,
                          }}
                          onPurchase={handlePurchaseCreditPack}
                          isPurchasing={isProcessing}
                        />
                      ))}
                    </div>
                  </div>

                  {/* Workspace Allocations */}
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-6">Workspace Allocations</h2>
                    <p className="text-gray-600 mb-6">
                      Allocate your credits to workspaces. You can revoke unspent credits at any time.
                    </p>
                    <div className="grid gap-6 md:grid-cols-2">
                      {workspaces.map((ws) => {
                        const credits = workspaceCredits[ws.workspaceId];
                        if (!credits) return null;

                        return (
                          <WorkspaceCreditDisplay
                            key={ws.workspaceId}
                            workspace={{
                              workspaceId: ws.workspaceId,
                              workspaceName: ws.name,
                              totalCredits: credits.totalCredits,
                              creditsRemaining: credits.creditsRemaining,
                              creditsUsed: credits.totalCredits - credits.creditsRemaining,
                              usageByService: credits.byService,
                            }}
                            onAllocate={() => handleAllocateCredits(ws.workspaceId)}
                            onRevoke={() => handleRevokeCredits(ws.workspaceId)}
                            showUsageBreakdown={true}
                          />
                        );
                      })}
                    </div>
                  </div>
                </>
              )}

              {/* Usage Tab */}
              {activeTab === 'usage' && (
                <>
                  <div className="space-y-8">
                    {/* Transaction History */}
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900 mb-2">Transaction History</h2>
                      <p className="text-gray-600 mb-6">
                        View your complete credit allocation and usage history.
                      </p>

                      <TransactionHistory
                        transactions={transactions}
                        workspaces={workspaces.map(ws => ({ workspaceId: ws.workspaceId, name: ws.name }))}
                        isLoading={isLoading}
                      />
                    </div>

                    {/* Workspace Usage Analytics */}
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900 mb-2">Workspace Usage</h2>
                      <p className="text-gray-600 mb-6">
                        View detailed credit usage across all your workspaces.
                      </p>

                      <div className="grid gap-6">
                        {workspaces.map((ws) => {
                          const credits = workspaceCredits[ws.workspaceId];
                          if (!credits) return null;

                          return (
                            <WorkspaceCreditDisplay
                              key={ws.workspaceId}
                              workspace={{
                                workspaceId: ws.workspaceId,
                                workspaceName: ws.name,
                                totalCredits: credits.totalCredits,
                                creditsRemaining: credits.creditsRemaining,
                                creditsUsed: credits.totalCredits - credits.creditsRemaining,
                                usageByService: credits.byService,
                              }}
                              showUsageBreakdown={true}
                            />
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </main>
      </div>

      {/* Credit Allocation Modal */}
      <CreditAllocationModal
        isOpen={allocationModal.isOpen}
        onClose={() => setAllocationModal({ isOpen: false, workspaceId: null, workspaceName: '' })}
        onAllocate={handleModalAllocate}
        workspaceName={allocationModal.workspaceName}
        maxCredits={userCredits?.creditsAvailable || 0}
        isAllocating={isProcessing}
      />

      {/* Credit Revocation Modal */}
      <CreditRevocationModal
        isOpen={revocationModal.isOpen}
        onClose={() => setRevocationModal({ isOpen: false, workspaceId: null, workspaceName: '', maxCredits: 0 })}
        onRevoke={handleModalRevoke}
        workspaceName={revocationModal.workspaceName}
        maxCredits={revocationModal.maxCredits}
        isRevoking={isProcessing}
      />

      {/* Credit Purchase Modal (Stripe) */}
      <CreditPurchaseModal
        isOpen={purchaseModal.isOpen}
        onClose={handlePurchaseCancel}
        onSuccess={handlePurchaseSuccess}
        clientSecret={purchaseModal.clientSecret}
        pack={purchaseModal.pack}
      />

      {/* Subscription Purchase Modal (Stripe) */}
      <SubscriptionPurchaseModal
        isOpen={subscriptionModal.isOpen}
        onClose={handleSubscriptionCancel}
        onSuccess={handleSubscriptionSuccess}
        clientSecret={subscriptionModal.clientSecret}
        tier={subscriptionModal.tier}
      />

      {/* Subscription Cancellation Modal */}
      <SubscriptionCancellationModal
        isOpen={cancelModal.isOpen}
        onClose={() => setCancelModal({ isOpen: false })}
        onConfirm={handleConfirmCancellation}
        isProcessing={isProcessing}
        periodEnd={_subscription?.current_period_end}
      />
    </div>
  );
}

